import { clamp, easeInOutCubic } from "@ui/lib/math";

// Fanned-deck geometry, fitted to Figma node 7:2767. The design's back cards are 3D-perspective
// renders (baked bitmaps in Figma): each card is turned about its own vertical axis (rotateY)
// under a per-card perspective camera, then scaled and slid left — there is NO in-plane rotation.
// Per rank we therefore drive two nested transforms:
//   inner: perspective(P) rotateY(phi)   — the 3D turn about the card centre
//   outer: translateX + scale            — post-projection placement in the fan
// The keyframe values below were least-squares fitted to the exact quad corners measured from the
// design's card silhouettes (max corner error < 0.7px). Rank 0 is the upright front card, ranks
// 1-3 fan back-left, rank -1 is the card leaving the front, rank 4+ is the hidden back of the
// deck. Values are interpolated between keyframes from a single continuous `position`; ranks wrap
// so the deck loops infinitely with the seam hidden in the transparent zone (rank <= -1 / >= 4).
// Cards share a common vertical centre in the design, so there is no Y channel.

// Ranks -1..-0.5 describe the front card quietly leaving the deck: a gentle page-turn hint
// (small negative rotateY), a whisper of rightward drift, shrinking slightly and fading early so
// attention goes to the incoming card rather than the departing one.
const RANK_KEYFRAMES = [-1, -0.5, 0, 1, 2, 3, 4];
// Projected card-centre offset, as a percentage of one card's width (fitted tx / 184).
const X_PERCENT = [4, 2, 0, -53.38, -94.33, -132.08, -132.08];
const SCALE = [0.98, 0.99, 1, 0.8512, 0.7454, 0.5993, 0.5993];
// 3D turn about the card's vertical axis; positive = right edge recedes (matches the design).
const ROTATE_Y_DEG = [-30, -14, 0, 25.61, 22.34, 22.34, 22.34];
// Perspective distance in live px, tuned to the lg card (192px wide vs 184 in the design file).
const PERSPECTIVE_PX = [291, 291, 291, 291, 249.5, 249.5, 249.5];
const OPACITY = [0, 0.75, 1, 1, 1, 1, 0];

// Mount intro: cards peel off the front stack one after another in a smooth overlapping cascade —
// each card starts as the previous one is partway through, so the deck flows open without pauses.
const INTRO_WINDOW = 0.4;
const INTRO_STAGGER = 0.3;
// Extra page-turn rotation (deg) layered on the intro: each back card sweeps through an
// exaggerated rotateY arc mid-window (sin bump, zero at both ends) before settling into the fan.
const INTRO_TURN_DEG = 55;

function interpolate(rank: number, outputs: number[]) {
  if (rank <= RANK_KEYFRAMES[0]) return outputs[0];
  if (rank >= RANK_KEYFRAMES[RANK_KEYFRAMES.length - 1]) return outputs[outputs.length - 1];

  let segment = 0;
  while (rank > RANK_KEYFRAMES[segment + 1]) segment++;

  const span = RANK_KEYFRAMES[segment + 1] - RANK_KEYFRAMES[segment];
  const t = (rank - RANK_KEYFRAMES[segment]) / span;

  return outputs[segment] + (outputs[segment + 1] - outputs[segment]) * t;
}

// Wrap an integer index into [0, total).
export function wrapIndex(index: number, total: number) {
  return ((index % total) + total) % total;
}

// Map a raw rank into [-1, total - 1) so every card has a unique representative and the wrap seam
// lands between the (faded) exit slot and the (hidden) back of the deck. A single-card deck has
// no exit slot — its card must keep the front rank, not the (transparent) exit rank.
export function wrapRank(rank: number, total: number) {
  const exitSlots = total > 1 ? 1 : 0;

  return wrapIndex(rank + exitSlots, total) - exitSlots;
}

// Per-card intro progress: 0 = stacked on the front slot, 1 = resting fan pose. Ranks 1..3 start
// one stagger apart (deeper cards later); the deepest window ends exactly when the clock hits 1,
// so any rank is guaranteed to be settled (progress 1) once the intro completes.
export function introProgress(rank: number, clock: number) {
  const start = Math.min(Math.max(rank - 1, 0), 2) * INTRO_STAGGER;

  return easeInOutCubic(clamp((clock - start) / INTRO_WINDOW));
}

// Resting turn scaled by intro progress, plus the page-turn arc while a back card peels off the
// stack (the sin bump is zero at both window ends, so the fitted fan pose is untouched once
// settled; the gate keeps the front and exiting cards out of the arc).
export function introRotateY(rank: number, progress: number) {
  return fanRotateY(rank) * progress + INTRO_TURN_DEG * Math.sin(Math.PI * progress) * clamp(rank);
}

export const fanX = (rank: number) => interpolate(rank, X_PERCENT);
export const fanScale = (rank: number) => interpolate(rank, SCALE);
export const fanRotateY = (rank: number) => interpolate(rank, ROTATE_Y_DEG);
export const fanPerspective = (rank: number) => interpolate(rank, PERSPECTIVE_PX);
export const fanOpacity = (rank: number) => interpolate(rank, OPACITY);
// The exiting card (negative rank) flips toward the viewer, so it must stay above the deck.
export const fanZIndex = (rank: number) => (rank < 0 ? 110 : Math.round(100 - rank * 10));
